import { compileControlChanges } from "./controlDrafting.js";
import { PROPOSAL_AGENTS, proposalDocument } from "./controlProposal.js";
import { valueAtPointer } from "../pages/agentPolicy/documentDiff.js";
import { FEATURE_ROWS } from "../pages/agentPolicy/chat/changes.js";

export function controlCapabilities(today) {
    return PROPOSAL_AGENTS.filter((agentId) => today[agentId]).map((agentId) => {
        const pointers = new Set(today[agentId].catalog.settings.map((entry) => entry.pointer));
        const decisions = today[agentId].catalog.settings.find((entry) => entry.pointer === "/rules/prefix_rules")?.ruleDecisions;
        return { agentId,
            commandApproval: pointers.has(agentId === "codex" ? "/rules/prefix_rules" : "/permissions/ask") && (agentId !== "codex" || !decisions?.length || decisions.includes("prompt")),
            commandBlock: pointers.has(agentId === "codex" ? "/rules/prefix_rules" : "/permissions/deny") && (agentId !== "codex" || !decisions?.length || decisions.includes("forbidden")),
            reviewerGuidance: agentId !== "github-copilot" && pointers.has(agentId === "codex" ? "/guardian_extra_policy" : "/autoMode/environment"),
            mcpServerBlock: agentId !== "codex" && pointers.has("/deniedMcpServers"),
        };
    });
}

const topic = (change) => change.kind === "permission" ? `permission:${change.targetKind}:${change.target}`
    : change.kind === "reviewer_guidance" ? `guidance:${change.text}`
        : change.kind === "mcp_server" ? `mcp:${change.name}` : `feature:${change.featureId}`;
function pointersFor(change, agentId) {
    if (change.kind === "permission") return agentId === "codex" ? ["/rules/prefix_rules"] : ["/permissions/allow", "/permissions/ask", "/permissions/deny"];
    if (change.kind === "reviewer_guidance") return [agentId === "codex" ? "/guardian_extra_policy" : "/autoMode/environment"];
    if (change.kind === "mcp_server") return ["/deniedMcpServers"];
    return [FEATURE_ROWS.find((row) => row.id === change.featureId)?.map[agentId]?.[0]].filter(Boolean);
}
const stable = (value) => Array.isArray(value) ? value.map(stable) : value && typeof value === "object" ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, stable(value[key])])) : value;
const comparable = (value, permission) => JSON.stringify(permission && Array.isArray(value) ? value.map((row) => JSON.stringify(stable(row))).sort() : stable(value));
const changesValues = (session, request) => compileControlChanges(session, [request], session.proposal.revision).some((operation) => {
    const before = valueAtPointer(proposalDocument(session.proposal, session.today, operation.agentId), operation.pointer);
    return before.found !== (operation.operation === "set") || comparable(before.value, request.kind === "permission") !== comparable(operation.value, request.kind === "permission");
});
export function describeControlChange(change) {
    if (change.kind === "permission") return `${change.decision === "ask" ? "Request approval" : change.decision === "block" ? "Block" : "Allow"}: ${change.target}`;
    if (change.kind === "reviewer_guidance") return "Add the proposed reviewer guidance";
    if (change.kind === "mcp_server") return `Block MCP server: ${change.name}`;
    return `Turn off ${FEATURE_ROWS.find((row) => row.id === change.featureId)?.label || change.featureId}`;
}

const cache = new WeakMap();
// These are unselected options derived from current saved controls, not new
// recommendations or automatic scope expansion. Every click uses the compiler.
export function additionalControlOptions(session) {
    const cached = cache.get(session.proposal);
    if (cached?.today === session.today) return cached.options;
    const history = session.proposal.structuredChanges || [];
    const explicit = new Set(history.filter((row) => ["permission", "reviewer_guidance", "mcp_server", "feature"].includes(row.kind))
        .flatMap((row) => row.agentIds.map((agentId) => `${agentId}:${topic(row)}`)));
    const seen = new Set(), barriers = new Set(), candidates = [];
    for (const original of [...history].reverse()) {
        for (const source of original.agentIds) {
            if (original.kind === "setting") { barriers.add(`${source}:${original.pointer}`); continue; }
            if (!["permission", "reviewer_guidance", "mcp_server", "feature"].includes(original.kind)) continue;
            const key = `${source}:${topic(original)}`, pointers = pointersFor(original, source);
            if (seen.has(key) || (original.kind === "reviewer_guidance" && barriers.has(`${source}:guidance`)) || pointers.some((pointer) => barriers.has(`${source}:${pointer}`))) continue;
            seen.add(key);
            if (original.kind === "reviewer_guidance" && original.operation === "replace") barriers.add(`${source}:guidance`);
            if (["remove", "reset"].includes(original.operation) || original.decision === "remove") continue;
            if (!(session.proposal.edits[source] || []).some((edit) => pointers.includes(edit.pointer))) continue;
            try {
                if (changesValues(session, { ...original, agentIds: [source] })) continue;
            } catch { continue; }
            candidates.push(original);
            if (candidates.length >= 30) break;
        }
        if (candidates.length >= 30) break;
    }
    const options = [];
    for (const agentId of PROPOSAL_AGENTS.filter((id) => session.today[id])) {
        const changes = new Map();
        for (const original of [...candidates].reverse()) {
            if (explicit.has(`${agentId}:${topic(original)}`)) continue;
            // Do not replace an intentionally different target-agent draft,
            // including imported exact-value drafts without semantic history.
            if ((session.proposal.edits[agentId] || []).some((edit) => pointersFor(original, agentId).includes(edit.pointer))) continue;
            const request = { ...original, agentIds: [agentId], reason: "Apply the same proposed control to this agent type.",
                ...(original.kind === "reviewer_guidance" ? { operation: "append" } : {}) };
            try {
                if (changesValues(session, request)) changes.set(topic(request), request);
            } catch { /* Unsupported controls are never offered for this target. */ }
        }
        if (changes.size) options.push({ agentId, changes: [...changes.values()], descriptions: [...changes.values()].map(describeControlChange) });
    }
    cache.set(session.proposal, { today: session.today, options });
    return options;
}
